import type { Page, PageWidgetLike } from '@efdigital/ll-addon-data';
import type { PageWidget } from '@efdigital/ll-addon-data';
import { PageWidgetType } from '@efdigital/ll-addon-data';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import {
  type PageContext,
  PageManager,
} from '@efdigital/ll-admin-v2/lib/page-manager';
import { generateRandomSlug } from '@efdigital/ll-admin-v2/lib/random-slug';
import { fn } from '@ember/helper';
import { get } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import BsDropdown from 'ember-bootstrap/components/bs-dropdown';
import { cell, resource, use } from 'ember-resources';

import { DragToSort } from '../drag-to-sort.gts';
import type { WidgetEditor } from './page-widget-signature';
import { AudioWidgetEditor } from './page-widgets/audio.gts';
import { CalendarWidgetEditor } from './page-widgets/calendar.gts';
import { EmbedHtmlWidgetEditor } from './page-widgets/embed-html.gts';
import { EmbedVideoWidgetEditor } from './page-widgets/embed-video.gts';
import { ExternalLinkWidgetEditor } from './page-widgets/external-link.gts';
import { FilesAndFoldersWidgetEditor } from './page-widgets/files-and-folders.gts';
import { FlourishWidgetEditor } from './page-widgets/flourish.gts';
import { ImageWidgetEditor } from './page-widgets/image.gts';
import { MarkdownWidgetEditor } from './page-widgets/markdown.gts';
import { QuizWidgetEditor } from './page-widgets/quiz.gts';
import { ResourceWidgetEditor } from './page-widgets/resource.gts';

const WIDGET_EDITORS: Record<PageWidgetType, WidgetEditor> = {
  [PageWidgetType.Markdown]: MarkdownWidgetEditor,
  [PageWidgetType.Image]: ImageWidgetEditor,
  [PageWidgetType.EmbedVideo]: EmbedVideoWidgetEditor,
  [PageWidgetType.Audio]: AudioWidgetEditor,
  [PageWidgetType.Resource]: ResourceWidgetEditor,
  [PageWidgetType.Quiz]: QuizWidgetEditor,
  [PageWidgetType.FilesAndFolders]: FilesAndFoldersWidgetEditor,
  [PageWidgetType.Calendar]: CalendarWidgetEditor,
  [PageWidgetType.ExternalLink]: ExternalLinkWidgetEditor,
  [PageWidgetType.Flourish]: FlourishWidgetEditor,
  [PageWidgetType.EmbedHtml]: EmbedHtmlWidgetEditor,
};

const WIDGET_TYPES = Object.entries(WIDGET_EDITORS)
  .filter(([key]) => key !== `${PageWidgetType.Quiz}`)
  .map(([key, value]) => {
    return {
      key,
      label: value.label,
      icon: value.icon,
    };
  });

const eq = (a: number, b: number) => a === b;

export class EditPageWidgets extends Component<
  EditRecordSignature<Page, { pageContext: PageContext }>
> {
  @service declare store: Store;

  @tracked selectedIndex = -1;

  onEdit = () => {
    this.selectedIndex = -1;
  };

  removeWidgetAt = (widgets: PageWidget[], index: number) => {
    const copy = [...widgets];
    copy.splice(index, 1);
    widgets.splice(0, widgets.length, ...copy.map((w) => ({ ...w })));
  };

  newWidget = (widgetType: PageWidgetType) => {
    const editor = WIDGET_EDITORS[widgetType];
    const widget = {
      wid: generateRandomSlug(),
      ...structuredClone(editor.widgetData),
    } as PageWidgetLike;
    this.args.item.widgets = [...this.args.item.widgets, widget as PageWidget];
    this.selectedIndex = this.args.item.widgets.length - 1;
  };

  updateWidgets = (widgets: PageWidget[]) => {
    this.args.item.widgets.splice(
      0,
      widgets.length,
      ...widgets.map((w) => ({ ...w }))
    );
    this.selectedIndex = -1;
  };

  setWidgetTitle = (widget: PageWidget, event: Event) => {
    const target = event.target as HTMLInputElement;
    widget.title = target.value;
  };

  editWidget = (index: number) => {
    this.selectedIndex = index;
  };

  @use pageManager = resource(() => {
    const value = cell<PageManager>(
      new PageManager(this.store, this.args.item, this.args.pageContext)
    );
    return () => value.current;
  });

  <template>
    <EditRecord
      @editLabel="Manage Widgets"
      @item={{@item}}
      @context={{@context}}
      @onEdit={{this.onEdit}}
      @maxDepth={{2}}
      ...attributes
    >
      <:form as |uniqueId isDisabled isReadOnly|>
        <fieldset disabled={{isDisabled}} class="mb-0">
          <legend class="gud-eyebrow">Content</legend>
          {{#unless @item.widgets.length}}
            <p>You can add content to this page using widgets, such as text,
              images, resources and more.</p>
          {{/unless}}
          {{#if isReadOnly}}
            <div class="list-group mb-3">
              {{#each @item.widgets as |widget|}}
                {{#let (get WIDGET_EDITORS widget.widgetType) as |widgetType|}}
                  <div class="list-group-item">
                    <iconify-icon inline icon={{widgetType.icon}} />
                    {{#if widget.title}}
                      {{widget.title}}
                    {{else}}
                      {{widgetType.label}}
                    {{/if}}
                  </div>
                {{/let}}
              {{/each}}
            </div>
          {{else}}
            <div class="list-group mb-3 gap-1">
              <DragToSort
                @items={{@item.widgets}}
                @updateItems={{this.updateWidgets}}
              >
                <:item as |dropZone draggable widget index|>
                  {{#let
                    (get WIDGET_EDITORS widget.widgetType)
                    as |widgetType|
                  }}
                    {{#let (eq this.selectedIndex index) as |isEditing|}}
                      <div class="{{if isEditing 'shadow rounded-bottom-1'}}">
                        <div class="input-group" {{dropZone index}}>
                          <span
                            class="input-group-text justify-content-center fw-bold drag-handle w-3rem fs-4
                              {{if isEditing 'rounded-bottom-0'}}"
                            {{draggable widget index}}
                            title="Drag to reorder"
                          >
                            <span
                              class="visually-hidden"
                            >{{widgetType.label}}</span>
                            <iconify-icon inline icon={{widgetType.icon}} />
                          </span>
                          {{#if isEditing}}
                            <button
                              aria-label="Stop editing"
                              class="btn btn-primary rounded-0 fs-4 px-3 py-0"
                              type="button"
                              {{on "click" (fn this.editWidget -1)}}
                            ><iconify-icon
                                inline
                                icon="ph:check-bold"
                              /></button>
                          {{else}}
                            <button
                              aria-label="Edit widget"
                              class="btn btn-outline-primary rounded-0 fs-4 px-3 py-0"
                              type="button"
                              {{on "click" (fn this.editWidget index)}}
                            ><iconify-icon
                                inline
                                icon="ph:pencil-ruler"
                              /></button>
                          {{/if}}
                          <input
                            type="text"
                            class="form-control list-group-item"
                            value={{widget.title}}
                            aria-label="Page title"
                            required
                            {{debounce (fn this.setWidgetTitle widget)}}
                          />
                          <button
                            aria-label="Remove widget"
                            class="btn btn-outline-primary rounded-end-1
                              {{if isEditing 'rounded-bottom-0'}}"
                            type="button"
                            {{on
                              "click"
                              (fn this.removeWidgetAt @item.widgets index)
                            }}
                          ><iconify-icon inline icon="ph:trash-bold" /></button>
                        </div>
                        {{#if isEditing}}
                          <div
                            class="card rounded-top-0 border-top-0 bg-primary-subtle ll-widget-editor-{{widget.widgetType}}"
                          >
                            {{#if widgetType.component}}
                              <widgetType.component
                                @model={{@item}}
                                @widget={{widget}}
                                @pageManager={{this.pageManager}}
                              />
                            {{/if}}
                          </div>
                        {{/if}}
                      </div>
                    {{/let}}
                  {{/let}}
                </:item>
              </DragToSort>
            </div>
          {{/if}}
        </fieldset>
      </:form>
      <:buttons as |uniqueId isReadOnly|>
        {{#unless isReadOnly}}
          <BsDropdown as |dd|>
            <dd.button @size="sm">
              Add Widget
              <span class="caret"></span>
            </dd.button>
            <dd.menu as |menu|>
              {{#each WIDGET_TYPES as |widgetType|}}
                <menu.item>
                  <button
                    class="dropdown-item"
                    type="button"
                    {{on "click" (fn this.newWidget widgetType.key)}}
                  >
                    <iconify-icon
                      inline
                      icon={{widgetType.icon}}
                      class="me-2"
                    />
                    {{widgetType.label}}
                  </button>
                </menu.item>
              {{/each}}
            </dd.menu>
          </BsDropdown>
        {{/unless}}
      </:buttons>
    </EditRecord>
  </template>
}
